import { useState } from 'react';
import { useLocation } from 'react-router';
import {
  Box, Button, Card, Flex, Separator, Text, Textarea,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import type { FormErrors } from '@customTypes/errors';
import { parseStringNumberToFloat } from '@utils/formats';
import api from '@services/api';
import CurrencyField from '@components/CurrencyInput';
import ErrorLabel from '@components/ErrorLabel';
import { datadogRum } from '@datadog/browser-rum';

import useGetAvailableDispositionOptions from '../useGetAvailableDispositionOptions';
import DispositionOptionSelect from './DispositionOptionSelect';
import ConfirmNextCampaignExecutionDialog from './ConfirmNextCampaignExecutionDialog';
import StayInPageDialog from './StayInPageDialog';
import type { DispositionOptionType } from '../types';

const BEHAVIOUR_CLOSE_DEAL = 4;

const DispositionForm = () => {
  const location = useLocation();
  const customerInteractionId = location.state.customerInteractionId;
  const funnelId = location.state.funnelId;

  const [loading, setLoading] = useState(false);
  const [showNextCampaignExecutionDialog, setShowNextCampaignExecutionDialog] = useState(false);
  const [showStayInPageDialog, setShowStayInPageDialog] = useState(false);
  const [selectedDispositionOption, setSelectedDispositionOption] = useState<DispositionOptionType | null>(null);
  const [description, setDescription] = useState('');
  const [negotiatedValue, setNegotiatedValue] = useState('');
  const [negotiatedAmount, setNegotiatedAmount] = useState('');
  const [installmentValue, setInstallmentValue] = useState('');
  const [installmentCount, setInstallmentCount] = useState('');
  const [errors, setErrors] = useState<FormErrors | null>(null);

  const { dispositionOptions } = useGetAvailableDispositionOptions(funnelId);

  const isCloseDeal = selectedDispositionOption?.behaviour === BEHAVIOUR_CLOSE_DEAL;

  const clearData = () => {
    setSelectedDispositionOption(null);
    setDescription('');
    setNegotiatedValue('');
    setNegotiatedAmount('');
    setInstallmentValue('');
    setInstallmentCount('');
    setErrors(null);
  };

  const handleWithInteractions = () => {
    if (location.pathname.includes('/meus-atendimentos')) {
      setShowStayInPageDialog(true);
      return;
    }
    setShowNextCampaignExecutionDialog(true);
  };

  const onDispositionClick = async () => {
    setErrors(null);

    if (!selectedDispositionOption) {
      setErrors({ selected_option: ['Selecione uma opção de tabulação.'] });
      return;
    }

    if (parseStringNumberToFloat(negotiatedValue) <= 0 && selectedDispositionOption?.ask_negotiated_amount) {
      setErrors({ negotiated_value: ['O valor negociado é obrigatório.'] });
      return;
    }

    if (isCloseDeal) {
      if (parseStringNumberToFloat(negotiatedAmount) <= 0) {
        setErrors({ negotiated_amount: ['Informe o valor total negociado.'] });
        return;
      }
      if (parseStringNumberToFloat(installmentValue) <= 0) {
        setErrors({ installment_value: ['Informe o valor da parcela.'] });
        return;
      }
      const count = parseInt(installmentCount, 10);
      if (!count || count <= 0) {
        setErrors({ installment_count: ['Informe a quantidade de parcelas.'] });
        return;
      }
    }

    try {
      setLoading(true);

      const body: Record<string, unknown> = {
        outcome_option_id: selectedDispositionOption.id,
        outcome: description || undefined,
        attributes: negotiatedValue ? {
          negotiated_value: parseStringNumberToFloat(negotiatedValue),
        } : undefined,
      };

      if (isCloseDeal) {
        body.contract_data = {
          negotiated_amount: parseStringNumberToFloat(negotiatedAmount),
          installment_value: parseStringNumberToFloat(installmentValue),
          installment_count: parseInt(installmentCount, 10),
        };
      }

      const response = await api.post(`/customer_interaction/${customerInteractionId}/dispose`, body);
      if (response.status === 200) {
        handleWithInteractions();
      }
    } catch (error) {
      toast.error('Erro ao salvar tabulação. Tente novamente!');
      datadogRum.addError(new Error('Fail to create a disposition on customer interaction'), {
        customerInteractionId,
        selectedDispositionOption,
        description,
        negotiatedValue,
        error,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card.Root size="sm" variant="subtle" p={4}>
      <Text fontSize="md" fontWeight="bold">
        Tabulação
      </Text>
      <Separator mb={6} mt={1} />

      <Box mb={4} maxW={{ base: '100%', md: 400 }}>
        <DispositionOptionSelect
          options={dispositionOptions}
          value={selectedDispositionOption}
          onChange={setSelectedDispositionOption}
        />
        {errors?.selected_option && (
          <Flex direction="column">
            {errors.selected_option.map((error, index) => (
              <ErrorLabel key={index} text={error} />
            ))}
          </Flex>
        )}
      </Box>

      {selectedDispositionOption?.ask_negotiated_amount && (
        <Box mb={5} maxW={{ base: '100%', md: 400 }}>
          <CurrencyField
            value={negotiatedValue}
            onChange={setNegotiatedValue}
            label="Valor negociado"
            variant="outline"
            errors={errors?.negotiated_value}
          />
        </Box>
      )}

      {isCloseDeal && (
        <Box mb={5} p={4} border="1px solid" borderColor="border.muted" borderRadius="md">
          <Text fontSize="sm" fontWeight={600} mb={3}>
            Dados do contrato
          </Text>
          <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
            <Box flex={1}>
              <CurrencyField
                value={negotiatedAmount}
                onChange={setNegotiatedAmount}
                label="Valor total negociado"
                variant="outline"
                errors={errors?.negotiated_amount}
              />
            </Box>
            <Box flex={1}>
              <CurrencyField
                value={installmentValue}
                onChange={setInstallmentValue}
                label="Valor da parcela"
                variant="outline"
                errors={errors?.installment_value}
              />
            </Box>
            <Box flex={1} maxW={{ base: '100%', md: 160 }}>
              <Text fontSize="sm" fontWeight={500} mb={1}>
                Nº de parcelas
              </Text>
              <input
                type="number"
                min={1}
                placeholder="Ex: 12"
                value={installmentCount}
                onChange={(e) => setInstallmentCount(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--chakra-colors-border-muted)',
                  background: 'transparent',
                  fontSize: '14px',
                }}
              />
              {errors?.installment_count?.map((err, i) => (
                <ErrorLabel key={i} text={err} />
              ))}
            </Box>
          </Flex>
        </Box>
      )}

      <Textarea
        placeholder="Comentário..."
        rows={5}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={1000}
      />

      {errors?.general && (
        <Flex direction="column" my={4}>
          {errors.general.map((error, index) => (
            <ErrorLabel key={index} text={error} />
          ))}
        </Flex>
      )}

      <Flex justifyContent="flex-end" mt={4}>
        <Button bg="green.700" color="white" onClick={onDispositionClick} loading={loading}>
          Tabular
        </Button>
      </Flex>

      {showNextCampaignExecutionDialog && (
        <ConfirmNextCampaignExecutionDialog
          handleClose={() => setShowNextCampaignExecutionDialog(false)}
          afterSubmit={clearData}
        />
      )}

      {showStayInPageDialog && (
        <StayInPageDialog
          handleClose={() => setShowStayInPageDialog(false)}
        />
      )}
    </Card.Root>
  );
};

export default DispositionForm;
